import { useEffect, useRef, useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Checkbox } from '../../../../../components/ui/Checkbox';
import { DatePicker } from '../../../../../components/ui/DatePicker';
import { Dialog } from '../../../../../components/ui/Dialog';
import { FieldHelp } from '../../../../../components/ui/FieldHelp';
import { FormLine, FormRow, FormSection } from '../../../../../components/ui/Form';
import { Upload, type UploadFile } from '../../../../../components/ui/Upload';
import { Input } from '../../../../../components/ui/Input';
import { InputNumber } from '../../../../../components/ui/InputNumber';
import { Link } from '../../../../../components/ui/Link';
import { Radio } from '../../../../../components/ui/Radio';
import { Select } from '../../../../../components/ui/Select';
import { Switch } from '../../../../../components/ui/Switch';
import { Tabs } from '../../../../../components/ui/Tabs';
import { Tag } from '../../../../../components/ui/Tag';
import { Textarea } from '../../../../../components/ui/Textarea';
import { demoToday } from '../../../../../lib/demoTime';
import { StackRangeField } from '../../../../shared/StackRangeField';
import type { Coupon, CouponGroup } from '../../../../coupon-management/versions/v1/fixtures';
import { newcomerCouponId, referrerCouponId } from '../../../../shared/couponStore';
import type { RecommendationCard } from '../fixtures';
import styles from './RecommendationCardCreator.module.css';

/** 与 Figma 保持一致的默认按键色（blue-600）。 */
const defaultButtonColor = '#2878e8';

/* 多行输入统一走组件库 `Textarea`（Figma `2-Input 输入框 / Textarea`）。 */

export interface RecommendationCardCreatorProps {
  /** 编辑已存在的推荐卡时传入；不传即新建，此时两张奖励券尚未生成。 */
  card?: RecommendationCard;
  entities: readonly Coupon[];
  groups: readonly CouponGroup[];
  onBack: () => void;
  /** 上传、示例等尚未设计的能力统一走这里回显，避免误报成「预览」。 */
  onNotice: (message: string) => void;
  onOpenRelation: (couponId: number) => void;
  onOpenRules: (couponId: number) => void;
  onPreview: () => void;
  /**
   * 保存。参数是填写的活动名称。
   * 不做「保存并设置叠加规则」：一次保存会派生老生券与新生券两张券，
   * 按钮无法指明要配哪一张，配置入口交给列表的「叠加范围」两列（那里带具体券 id）。
   */
  onSave: (name: string) => void;
}

/* ---- 奖励设置（按截图补齐：老学员推荐奖励 / 新学员报名奖励两个分节）---- */

type RewardFormPatch = Partial<RewardFormState>;

interface RewardFormState {
  amount: string;
  applyCourse: boolean;
  applyGoods: boolean;
  /** 商城套餐仅老学员推荐奖励展示。 */
  applyPackage: boolean;
  consumeHours: string;
  courseUnlimited: boolean;
  courseValue: string;
  discountMode: 'fixed' | 'ladder';
  expiryDays: string;
  expiryEnd: string;
  expiryMode: 'fixed' | 'sinceClaim';
  expiryStart: string;
  expiryUnlimited: boolean;
  firstUseOnly: boolean;
  goodsUnlimited: boolean;
  goodsValue: string;
  itemAmount: number | null;
  orderAmount: number | null;
  refundReissue: boolean;
  rewardType: 'amount' | 'lesson';
  scopeFront: boolean;
  scopeOnline: boolean;
  threshold: 'none' | 'order' | 'item';
  /** 老学员为单选（直接使用 / 消课后使用）；新学员为「仅限新生首次报名使用」勾选。 */
  useCondition: 'direct' | 'consume';
}

function createRewardForm(): RewardFormState {
  return {
    amount: '',
    applyCourse: true,
    applyGoods: true,
    applyPackage: false,
    consumeHours: '',
    courseUnlimited: true,
    courseValue: '',
    discountMode: 'fixed',
    expiryDays: '',
    expiryEnd: '',
    expiryMode: 'sinceClaim',
    expiryStart: '',
    expiryUnlimited: false,
    firstUseOnly: false,
    goodsUnlimited: true,
    goodsValue: '',
    itemAmount: null,
    orderAmount: null,
    refundReissue: false,
    rewardType: 'amount',
    scopeFront: true,
    scopeOnline: true,
    threshold: 'none',
    useCondition: 'direct',
  };
}

/* 课程 / 物品候选：目标接口未定，先模拟同形态数据。 */
const courseOptions = [
  { label: '幼儿中国舞启蒙课（16课时）', value: 'course-1' },
  { label: '少儿中国舞考级课（24课时）', value: 'course-2' },
  { label: '少儿街舞体验课（4课时）', value: 'course-3' },
];
const goodsOptions = [
  { label: '舞蹈练功服（女款）', value: 'goods-1' },
  { label: '软底舞蹈鞋', value: 'goods-2' },
  { label: '练功背包', value: 'goods-3' },
];

const expiryHint = '用券时间设置为固定时间，编辑保存后对所有优惠券生效。设置为领券当日起多少天内可用，编辑保存后仅对后续领取的券生效。';

interface RewardSectionProps {
  entities: readonly Coupon[];
  form: RewardFormState;
  groups: readonly CouponGroup[];
  onChange: (patch: RewardFormPatch) => void;
  onOpenRelation: (couponId: number) => void;
  onOpenRules: (couponId: number) => void;
  /** 该节对应的奖励券；推荐卡保存后才存在，新建时为 undefined。 */
  rewardCoupon?: Coupon;
  /** referrer = 老学员推荐奖励；newcomer = 新学员报名奖励。两节字段大体相同，差异见各分支。 */
  variant: 'referrer' | 'newcomer';
}

function RewardSection({ entities, form, groups, onChange, onOpenRelation, onOpenRules, rewardCoupon, variant }: RewardSectionProps) {
  const isReferrer = variant === 'referrer';
  const rewardAmount = form.amount.trim() === '' ? '0' : form.amount.trim();
  const amountInput = (
    <Input ariaLabel={isReferrer ? '老学员奖励优惠金额' : '新学员奖励优惠金额'} onValueChange={(value) => onChange({ amount: value })} placeholder="请输入优惠金额" value={form.amount} />
  );

  return (
    <>
      <FormRow label="奖励类型" required>
        <Radio checked={form.rewardType === 'amount'} label="金额券" name={`reward-type-${variant}`} onCheckedChange={() => onChange({ rewardType: 'amount' })} />
        <Radio checked={form.rewardType === 'lesson'} label="课时券" name={`reward-type-${variant}`} onCheckedChange={() => onChange({ rewardType: 'lesson' })} />
      </FormRow>
      {isReferrer
        ? <FormRow hint={`每推荐成功1人，奖励${rewardAmount}元。`} label="优惠金额" layout="stack" required>
          <FormLine>
            <Radio checked={form.discountMode === 'fixed'} label="固定优惠" name={`discount-mode-${variant}`} onCheckedChange={() => onChange({ discountMode: 'fixed' })} />
            <Radio checked={form.discountMode === 'ladder'} label="阶梯优惠" name={`discount-mode-${variant}`} onCheckedChange={() => onChange({ discountMode: 'ladder' })} />
          </FormLine>
          <FormLine>{amountInput}</FormLine>
        </FormRow>
        : <FormRow label="优惠金额" required>{amountInput}</FormRow>}
      <FormRow help={expiryHint} helpLabel="有效期说明" hint={expiryHint} label="有效期" layout="stack" required>
        <FormLine>
          <Radio aria-label="固定时间有效期" checked={form.expiryMode === 'fixed'} name={`expiry-mode-${variant}`} onCheckedChange={() => onChange({ expiryMode: 'fixed' })} />
          <DatePicker ariaLabel="有效期开始时间" className={styles.dateInput} onValueChange={(value) => onChange({ expiryMode: 'fixed', expiryStart: value })} placeholder="开始日期" today={demoToday} value={form.expiryStart} />
          <span aria-hidden="true" className={styles.rangeDash}>-</span>
          <DatePicker ariaLabel="有效期结束时间" className={styles.dateInput} onValueChange={(value) => onChange({ expiryMode: 'fixed', expiryEnd: value })} placeholder="结束日期" today={demoToday} value={form.expiryEnd} />
          <Checkbox checked={form.expiryUnlimited} label="不限" onCheckedChange={(checked) => onChange({ expiryUnlimited: checked })} />
        </FormLine>
        <FormLine>
          <Radio aria-label="领券当日起算有效期" checked={form.expiryMode === 'sinceClaim'} name={`expiry-mode-${variant}`} onCheckedChange={() => onChange({ expiryMode: 'sinceClaim' })} />
          <span className={styles.inlineSentence}>
            <span>领券当日起</span>
            <Input ariaLabel="领券当日起可用天数" className={styles.inlineInput} onValueChange={(value) => onChange({ expiryDays: value, expiryMode: 'sinceClaim' })} placeholder="请输入" value={form.expiryDays} />
            <span>天内可用</span>
          </span>
        </FormLine>
      </FormRow>
      <FormRow label="使用范围" required>
        <Checkbox checked={form.scopeFront} label="前台报名" onCheckedChange={(checked) => onChange({ scopeFront: checked })} />
        <Checkbox checked={form.scopeOnline} label="线上报名" onCheckedChange={(checked) => onChange({ scopeOnline: checked })} />
      </FormRow>
      <FormRow label="使用门槛" layout="stack" required>
        <FormLine>
          <Radio checked={form.threshold === 'none'} label="无门槛" name={`threshold-${variant}`} onCheckedChange={() => onChange({ threshold: 'none' })} />
        </FormLine>
        <FormLine>
          <Radio aria-label="订单满额可用" checked={form.threshold === 'order'} name={`threshold-${variant}`} onCheckedChange={() => onChange({ threshold: 'order' })} />
          <span>订单满</span>
          <InputNumber ariaLabel="订单满额金额" min={0} onValueChange={(value) => onChange({ orderAmount: value, threshold: 'order' })} value={form.orderAmount} />
          <span>元可用</span>
        </FormLine>
        <FormLine>
          <Radio aria-label="单个项目满额可用" checked={form.threshold === 'item'} name={`threshold-${variant}`} onCheckedChange={() => onChange({ threshold: 'item' })} />
          <span>单个项目满</span>
          <InputNumber ariaLabel="单个项目满额金额" min={0} onValueChange={(value) => onChange({ itemAmount: value, threshold: 'item' })} value={form.itemAmount} />
          <span>元可用</span>
        </FormLine>
      </FormRow>
      <FormRow label="适用于" layout="stack" required>
        <FormLine>
          <Checkbox checked={form.applyCourse} label="课程" onCheckedChange={(checked) => onChange({ applyCourse: checked })} />
          <Checkbox checked={form.applyGoods} label="物品" onCheckedChange={(checked) => onChange({ applyGoods: checked })} />
          {isReferrer ? (
            <>
              <Checkbox checked={form.applyPackage} label="商城套餐" onCheckedChange={(checked) => onChange({ applyPackage: checked })} />
              <FieldHelp content="商城套餐暂未开放，敬请期待。" label="商城套餐说明" />
            </>
          ) : null}
        </FormLine>
        {form.applyCourse ? (
          <FormLine>
            <span>课程</span>
            <Select
              ariaLabel="适用课程"
              disabled={form.courseUnlimited}
              onValueChange={(value) => onChange({ courseValue: value })}
              options={courseOptions}
              placeholder="请选择"
              value={form.courseValue}
            />
            <Checkbox checked={form.courseUnlimited} label="不限" onCheckedChange={(checked) => onChange({ ...(checked ? { courseValue: '' } : {}), courseUnlimited: checked })} />
          </FormLine>
        ) : null}
        {form.applyGoods ? (
          <FormLine>
            <span>物品</span>
            <Select
              ariaLabel="适用物品"
              disabled={form.goodsUnlimited}
              onValueChange={(value) => onChange({ goodsValue: value })}
              options={goodsOptions}
              placeholder="请选择"
              value={form.goodsValue}
            />
            <Checkbox checked={form.goodsUnlimited} label="不限" onCheckedChange={(checked) => onChange({ ...(checked ? { goodsValue: '' } : {}), goodsUnlimited: checked })} />
          </FormLine>
        ) : null}
      </FormRow>
      {isReferrer ? (
        <FormRow label="使用条件" layout="stack" required>
          <Radio checked={form.useCondition === 'direct'} label="新学员使用优惠券报名后即可使用" name={`use-condition-${variant}`} onCheckedChange={() => onChange({ useCondition: 'direct' })} />
          <FormLine>
            <Radio aria-label="消课后可用" checked={form.useCondition === 'consume'} name={`use-condition-${variant}`} onCheckedChange={() => onChange({ useCondition: 'consume' })} />
            <span className={styles.inlineSentence}>
              <span>新学员报名并消课</span>
              <Input ariaLabel="消课数量" className={styles.inlineInput} onValueChange={(value) => onChange({ consumeHours: value, useCondition: 'consume' })} placeholder="请输入" value={form.consumeHours} />
              <span>课时/小时后方可使用</span>
            </span>
          </FormLine>
        </FormRow>
      ) : (
        <FormRow label="使用条件">
          <Checkbox checked={form.firstUseOnly} label="仅限新生首次报名使用" onCheckedChange={(checked) => onChange({ firstUseOnly: checked })} />
          <FieldHelp content="勾选后，仅首次报名的新生可以使用该优惠券。" label="使用条件说明" />
        </FormRow>
      )}
      <FormRow label="叠加生效范围：">
        {/*
         * 叠加关系不在这里配：和优惠券一样只展示结果，配置统一走「叠加规则设置」。
         * 推荐卡还没保存时两张奖励券都还不存在，只给一句提示。
         */}
        <StackRangeField
          coupon={rewardCoupon}
          entities={entities}
          groups={groups}
          onOpenRelation={onOpenRelation}
          onOpenRules={onOpenRules}
          unsavedHint={isReferrer ? '推荐卡保存后，可在「叠加规则设置」中设置老学员推荐奖励的叠加范围。' : '推荐卡保存后，可在「叠加规则设置」中设置新学员报名奖励的叠加范围。'}
        />
      </FormRow>
      <FormRow help="开启后，学员全额退费时已使用的优惠券会自动返还。" helpLabel="全额退费返券说明" label="全额退费返券">
        <Switch checked={form.refundReissue} label="全额退费返券" onCheckedChange={(checked) => onChange({ refundReissue: checked })} />
      </FormRow>
    </>
  );
}

export function RecommendationCardCreator({ card, entities, groups, onBack, onNotice, onOpenRelation, onOpenRules, onPreview, onSave }: RecommendationCardCreatorProps) {
  const [formTab, setFormTab] = useState('activity');
  const [name, setName] = useState(card?.name ?? '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [intro, setIntro] = useState('');
  const [ctaText, setCtaText] = useState('立即报名');
  const [ctaColor, setCtaColor] = useState(defaultButtonColor);
  const [campusSelectable, setCampusSelectable] = useState(true);
  const [shareTitle, setShareTitle] = useState('');
  const [shareDesc, setShareDesc] = useState('');
  const [remark, setRemark] = useState('');
  const [activityImages, setActivityImages] = useState<readonly UploadFile[]>([]);
  const [shareIcons, setShareIcons] = useState<readonly UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState<UploadFile | null>(null);
  const [oldRewardForm, setOldRewardForm] = useState<RewardFormState>(createRewardForm);
  const [newRewardForm, setNewRewardForm] = useState<RewardFormState>(createRewardForm);
  const nextUploadId = useRef(0);
  const localObjectUrls = useRef(new Set<string>());

  useEffect(() => () => {
    localObjectUrls.current.forEach((url) => URL.revokeObjectURL(url));
    localObjectUrls.current.clear();
  }, []);

  const createUploadFiles = (files: readonly File[]) => files.map((file) => {
    const url = URL.createObjectURL(file);
    localObjectUrls.current.add(url);
    nextUploadId.current += 1;
    return { id: `local-upload-${nextUploadId.current}`, name: file.name, url };
  });

  const removeUploadFile = (files: readonly UploadFile[], id: string, update: (files: readonly UploadFile[]) => void) => {
    const target = files.find((file) => file.id === id);
    if (target?.url) {
      URL.revokeObjectURL(target.url);
      localObjectUrls.current.delete(target.url);
    }
    if (previewImage?.id === id) setPreviewImage(null);
    update(files.filter((file) => file.id !== id));
  };

  const updateOldReward = (patch: RewardFormPatch) => setOldRewardForm((form) => ({ ...form, ...patch }));
  const updateNewReward = (patch: RewardFormPatch) => setNewRewardForm((form) => ({ ...form, ...patch }));

  /* 推荐卡保存后才会派生出两张奖励券，因此编辑态才有可展示的叠加范围。 */
  const referrerCoupon = card ? entities.find((coupon) => coupon.id === referrerCouponId(card.id)) : undefined;
  const newcomerCoupon = card ? entities.find((coupon) => coupon.id === newcomerCouponId(card.id)) : undefined;

  return <div className={styles.editor}>
    <div className={styles.editorLayout}>
      <section className={styles.previewColumn}>
        {/* 预览入口贴近被预览的对象：与手机壳左右对齐，不再和「保存」挤在底部。 */}
        <div className={styles.previewHead}>
          <h2 className={styles.previewTitle}>模拟效果</h2>
          <Button onClick={onPreview} type="default">预览</Button>
        </div>
        <div className={styles.phone}>
          <div className={styles.phoneHead}>
            <span aria-hidden="true">‹</span>
            <span>电子推荐卡</span>
            <span aria-hidden="true">•••</span>
          </div>
          <div className={styles.phoneBody}>
            {activityImages[0]?.url
              ? <img alt={activityImages[0].name} className={styles.phoneUploadedImage} src={activityImages[0].url} />
              : <div className={styles.phoneImageArea}>
                <span>此处为活动图片</span>
                <span className={styles.phoneImageHint}>最多添加10张图片</span>
              </div>}
          </div>
          <div className={styles.phoneFooter}>
            <span className={styles.phoneCta} style={{ background: ctaColor }}>{ctaText.trim() || '立即报名'}</span>
          </div>
        </div>
      </section>

      <section className={styles.formPanel}>
        <Tabs
          ariaLabel="电子推荐卡编辑页签"
          idPrefix="recommendation-card-form"
          onChange={setFormTab}
          tabs={[
            { id: 'activity', label: '活动设置' },
            {
              id: 'reward',
              label: <span className={styles.tabLabel}>
                奖励设置
                <Tag className={styles.requiredBadge} topic="dark" type="danger">必填</Tag>
              </span>,
            },
          ]}
          value={formTab}
        />
        <div aria-labelledby={`recommendation-card-form-${formTab}-tab`} className={styles.formContent} id={`recommendation-card-form-${formTab}-panel`} role="tabpanel">
          {formTab === 'reward'
            ? <>
              <FormSection help="老学员把电子推荐卡分享给新学员，新学员报名成功后老学员可获得的优惠券奖励。" helpLabel="老学员推荐奖励说明" title="老学员推荐奖励" />
              <RewardSection entities={entities} form={oldRewardForm} groups={groups} onChange={updateOldReward} onOpenRelation={onOpenRelation} onOpenRules={onOpenRules} rewardCoupon={referrerCoupon} variant="referrer" />
              <FormSection help="新学员领取电子推荐卡并报名后获得的优惠券奖励。" helpLabel="新学员报名奖励说明" title="新学员报名奖励" />
              <RewardSection entities={entities} form={newRewardForm} groups={groups} onChange={updateNewReward} onOpenRelation={onOpenRelation} onOpenRules={onOpenRules} rewardCoupon={newcomerCoupon} variant="newcomer" />
            </>
            : <>
              <FormSection title="活动内容" />
              <FormRow help="展示在电子推荐卡活动页与微信分享卡片上的活动名称。" helpLabel="活动名称说明" label="活动名称" required>
                <Input ariaLabel="活动名称" className={styles.fieldInput} maxLength={30} onValueChange={setName} placeholder="请输入活动名称" value={name} />
              </FormRow>
              <FormRow label="活动时间" required>
                <DatePicker ariaLabel="活动开始时间" className={styles.dateInput} onValueChange={setStartDate} placeholder="开始日期" today={demoToday} value={startDate} />
                <span aria-hidden="true" className={styles.rangeDash}>-</span>
                <DatePicker ariaLabel="活动结束时间" className={styles.dateInput} onValueChange={setEndDate} placeholder="结束日期" today={demoToday} value={endDate} />
              </FormRow>
              <FormRow label="活动图片设置">
                <Upload
                  accept="image/jpeg,image/png,image/webp"
                  ariaLabel="上传活动图片"
                  description="最多上传10张，建议宽度750像素。"
                  files={activityImages}
                  maxCount={10}
                  multiple
                  onExceed={(_, maxCount) => onNotice(`最多上传${maxCount}张图片`)}
                  onPreview={(id) => setPreviewImage(activityImages.find((file) => file.id === id) ?? null)}
                  onRemove={(id) => removeUploadFile(activityImages, id, setActivityImages)}
                  onSelect={(files) => setActivityImages((current) => [...current, ...createUploadFiles(files)])}
                  variant="picture-card"
                />
              </FormRow>
              <FormRow hint={<>新学员报名领取中展示。<Link onClick={() => onNotice('活动简介示例图尚未设计')}>查看示例</Link></>} label="活动简介">
                <Textarea
                  ariaLabel="活动简介"
                  maxLength={500}
                  onValueChange={setIntro}
                  placeholder="请输入活动简介"
                  showCount
                  value={intro}
                />
              </FormRow>

              <FormSection title="报名设置" />
              <FormRow label="按键名称" required>
                <Input ariaLabel="按键名称" className={styles.fieldInput} maxLength={8} onValueChange={setCtaText} value={ctaText} />
              </FormRow>
              <FormRow label="按键颜色">
                <label className={styles.colorField}>
                  <input aria-label="按键颜色" className={styles.colorInput} onChange={(event) => setCtaColor(event.target.value)} type="color" value={ctaColor} />
                </label>
              </FormRow>

              <div className={styles.campusBlock}>
                <div className={styles.campusLine}>
                  <span className={styles.campusLabel}>
                    新学员可选择意向报名校区
                    <FieldHelp content="开启后，新学员家长在领取电子推荐卡时需先选择意向报名校区。" label="意向报名校区说明" />
                  </span>
                  <Switch checked={campusSelectable} label="新学员可选择意向报名校区" onCheckedChange={setCampusSelectable} />
                </div>
                <p className={styles.campusNote}>注：新学员家长在领取电子推荐卡时需选择意向报名校区，方便学校后续操作，但增加了家长的操作步骤。</p>
              </div>

              <FormSection title="分享设置" />
              <FormRow help="微信分享卡片上展示的标题，建议不超过 20 字。" helpLabel="分享标题说明" label="分享标题" required>
                <Input ariaLabel="分享标题" className={styles.fieldInput} maxLength={30} onValueChange={setShareTitle} placeholder="电子推荐卡" value={shareTitle} />
              </FormRow>
              <FormRow help="微信分享卡片上展示的描述文案。" helpLabel="分享描述说明" hint="用户通过微信分享给朋友时，会自动显示页面描述。" label="分享描述" required>
                <Input ariaLabel="分享描述" className={styles.fieldInput} maxLength={50} onValueChange={setShareDesc} placeholder="请输入" value={shareDesc} />
              </FormRow>
              <FormRow help="微信分享卡片上展示的方形图标。" helpLabel="分享图标说明" hint="提示：建议图标大小200px*200px" label="分享图标">
                <Upload
                  accept="image/jpeg,image/png,image/webp"
                  ariaLabel="上传分享图标"
                  files={shareIcons}
                  maxCount={1}
                  onExceed={() => onNotice('分享图标只能上传1张')}
                  onPreview={(id) => setPreviewImage(shareIcons.find((file) => file.id === id) ?? null)}
                  onRemove={(id) => removeUploadFile(shareIcons, id, setShareIcons)}
                  onSelect={(files) => setShareIcons(createUploadFiles(files))}
                  size="compact"
                  variant="picture-card"
                />
              </FormRow>
              <FormRow hint={<>老师转发电子推荐卡活动给老学员，消息提醒中的备注信息。<Link onClick={() => onNotice('备注示例图尚未设计')}>查看示例</Link></>} label="备注">
                <Textarea
                  ariaLabel="备注"
                  maxLength={100}
                  onValueChange={setRemark}
                  placeholder="请输入"
                  showCount
                  value={remark}
                />
              </FormRow>
            </>}
        </div>
      </section>
    </div>
    <div className={styles.editFooter}>
      {/* 新建与编辑的保存路径一致：都只保存并回到列表。 */}
      <Button onClick={onBack} type="default">返回</Button>
      <Button onClick={() => onSave(name)}>保存</Button>
    </div>
    <Dialog onOpenChange={(open) => { if (!open) setPreviewImage(null); }} open={previewImage !== null} size="md" title={previewImage ? `预览 ${previewImage.name}` : '预览图片'}>
      {previewImage?.url ? <img alt={previewImage.name} className={styles.uploadPreviewImage} src={previewImage.url} /> : null}
    </Dialog>
  </div>;
}
